import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const UpdatePasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSessionValid, setIsSessionValid] = useState(false);

  useEffect(() => {
    // O Supabase lida com a troca de sessão automaticamente quando o usuário chega nesta página
    // via o link de e-mail. Apenas verificamos se há uma sessão ativa.
    supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) {
            setIsSessionValid(true);
        } else {
            // Se não houver sessão, o usuário pode ter chegado aqui sem o link de e-mail.
            // Em um ambiente de produção, o Supabase deve lidar com isso.
            // Aqui, assumimos que se a página carrega, a sessão está sendo processada.
            // Vamos confiar no Supabase para o fluxo de redefinição.
            setIsSessionValid(true); 
        }
    });
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error(t('A senha deve ter pelo menos 6 caracteres.'));
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error(t('As senhas não coincidem.'));
      return;
    }
    
    setLoading(true);

    try {
      // Atualiza a senha do usuário logado (a sessão foi trocada pelo Supabase ao carregar a página)
      const { error } = await supabase.auth.updateUser({ password });
      
      if (error) throw error;
      
      toast.success(t('Sua senha foi redefinida com sucesso!'));
      navigate('/'); // Redireciona para o Dashboard
      
    } catch (error) {
      let errorMessage = error instanceof Error ? error.message : t('Erro ao redefinir a senha.');
      
      if (errorMessage.includes('Password should be at least 6 characters')) {
          errorMessage = t('A senha deve ter pelo menos 6 caracteres.');
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isSessionValid && !loading) {
      // Isso pode acontecer se o link de redefinição expirou ou foi usado.
      return (
        <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark p-4 text-center">
            <p className="text-lg text-red-500">{t('Link de redefinição inválido ou expirado.')}</p>
        </div>
      );
  }

  return (
    <motion.div 
      className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div
        className="w-full max-w-md space-y-8"
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {/* Header Section */}
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-3 text-[#181411] dark:text-white">
            <span className="material-symbols-outlined text-4xl text-primary">
              lock_reset
            </span>
            <h1 className="text-3xl font-bold leading-tight tracking-[-0.015em]">{t('Redefinir Senha')}</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {t('Insira sua nova senha abaixo.')}
          </p>
        </div>

        {/* Form Card */}
        <div className="flex flex-col gap-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/50 p-6 sm:p-8">
          
          <form onSubmit={handleUpdatePassword} className="flex flex-col gap-4">
            
            {/* Nova Senha */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="new-password">{t('Nova Senha')}</label>
              <input 
                id="new-password"
                type="password" 
                placeholder="••••••••" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                required
              />
            </div>
            
            {/* Confirmar Nova Senha */}
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300" htmlFor="confirm-new-password">{t('Confirmar Nova Senha')}</label>
              <input 
                id="confirm-new-password"
                type="password" 
                placeholder="••••••••" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input w-full rounded-lg border-gray-300 dark:border-gray-700 bg-transparent px-4 py-2.5 text-[#181411] dark:text-white placeholder:text-gray-400 focus:border-primary focus:ring-primary/50"
                required
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              className="flex h-12 w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-gradient-to-r from-[#f98006] to-[#ffb800] text-sm font-bold text-white shadow-sm transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={loading || !isSessionValid}
            >
              {loading ? (
                <>
                  <span className="material-symbols-outlined animate-spin mr-2">sync</span>
                  {t('auth.loading')}
                </>
              ) : (
                t('Redefinir Senha')
              )}
            </motion.button>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default UpdatePasswordPage;